import React, {
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getThrowingDefaultContextValue } from '@shared/helpers/react.helpers';
import { is } from '@client/helpers/dom.guards';

type IntersectionElement =
  RefObject<Element | null> | Element | undefined | null;

interface Options extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

/**
 * Registers an element with a shared {@link IntersectionObserver} and returns an
 * unregister cleanup. Provided by {@link SharedIntersectionObserverProvider}.
 */
type RegisterIntersection = (
  element: Element,
  callback: (entry: IntersectionObserverEntry) => void,
) => () => void;

type SharedIntersectionObserverContextValue = {
  register: RegisterIntersection;
};

// Dev: accessing the default throws, so a missing provider fails fast.
// Prod: null — consumers detect the absence and fall back gracefully
// (see {@link useHasSharedIntersectionObserver}) instead of crashing users.
const SharedIntersectionObserverContext =
  createContext<SharedIntersectionObserverContextValue | null>(
    import.meta.env.DEV
      ? getThrowingDefaultContextValue<SharedIntersectionObserverContextValue>(
          '<HideOutsideViewport> must be rendered within a <SharedIntersectionObserverProvider>.',
        )
      : null,
  );

/**
 * Whether a {@link SharedIntersectionObserverProvider} is mounted above.
 * False only in production when the provider is missing — consumers should then
 * render their content unconditionally (no observer to cull with). In dev the
 * missing-provider default throws instead, surfacing the bug.
 */
export const useHasSharedIntersectionObserver = (): boolean => {
  const shared = useContext(SharedIntersectionObserverContext);
  // No IO support means no observer can cull; report absent so consumers keep
  // their always-visible fallback rather than relying on a dead observer.
  return hasIntersectionObserverSupport && shared !== null;
};

/**
 * Mounts a single {@link IntersectionObserver} shared by every
 * {@link useIntersectionObserverCallback} rendered underneath it, instead of one
 * observer per element. Required for any list rendering
 * {@link HideOutsideViewport}: a shared observer uses one uniform threshold for
 * all targets, so visibility re-evaluation is consistent, whereas per-element
 * observers are both wasteful and flaky (they occasionally fail to re-fire,
 * leaving rows stuck). Reading the context without a provider ancestor throws.
 */
export const SharedIntersectionObserverProvider: React.FC<{
  children: React.ReactNode;
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}> = ({ children, root = null, rootMargin = '0%', threshold = 0 }) => {
  const callbacks = useRef(
    new Map<Element, (entry: IntersectionObserverEntry) => void>(),
  );
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!hasIntersectionObserverSupport) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // root bounds is null in some unknown cases and would falsely report
          // the element as invisible — skip those entries (mirrors the
          // standalone observer below).
          if (entry.rootBounds) callbacks.current.get(entry.target)?.(entry);
        });
      },
      { root, rootMargin, threshold },
    );
    observerRef.current = observer;
    // Child effects run before parent effects, so anything registered during
    // the initial mount is waiting in the map — start observing it now.
    callbacks.current.forEach((_cb, element) => observer.observe(element));
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [root, rootMargin, threshold]);

  const register = useCallback<RegisterIntersection>((element, callback) => {
    callbacks.current.set(element, callback);
    observerRef.current?.observe(element);
    return () => {
      callbacks.current.delete(element);
      observerRef.current?.unobserve(element);
    };
  }, []);

  const value = useMemo(() => ({ register }), [register]);

  return (
    <SharedIntersectionObserverContext.Provider value={value}>
      {children}
    </SharedIntersectionObserverContext.Provider>
  );
};

/**
 * Registers an element with the {@link SharedIntersectionObserverProvider} and
 * invokes callback on every intersection change. A provider ancestor is
 * required: in dev the missing-provider default throws on access
 * (see {@link getThrowingDefaultContextValue}); in prod it is null and the
 * element is simply not observed. The observer's threshold/root/rootMargin are
 * defined by the provider.
 * @param elementRef
 * @param callback Pass undefined to stop observing
 */
export const useIntersectionObserverCallback = (
  elementRef: IntersectionElement,
  callback?: (entry: IntersectionObserverEntry) => void,
): void => {
  const shared = useContext(SharedIntersectionObserverContext);

  useEffect(() => {
    // Resolve the ref inside the effect: during render `ref.current` is still
    // null, and assigning it does not rerender, so reading at render can miss
    // initially visible rows.
    const target = getElement(elementRef);
    if (!hasIntersectionObserverSupport || !target || !callback || !shared)
      return;
    return shared.register(target, callback);
  }, [callback, elementRef, shared]);
};

/**
 * Get latest entry from intersection observer
 * @param elementRef Pass undefined to stop observing
 * @param options
 * @returns
 */
export const useIntersectionObserver = (
  elementRef?: IntersectionElement,
  options: Options = {},
): IntersectionObserverEntry | undefined => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const frozen = entry?.isIntersecting && options.freezeOnceVisible;

  const updateEntry = useCallback((entry: IntersectionObserverEntry): void => {
    setEntry(entry);
  }, []);

  // Only run the callback if the element is not frozen
  useIntersectionObserverCallback(
    elementRef,
    !frozen ? updateEntry : undefined,
  );

  return entry;
};

/**
 * Returns true when element is within the viewport.
 * If no element is passed it will always return true.
 * @param elementRef
 * @returns
 */
export const useIsVisible = (
  elementRef?: RefObject<Element | null>,
  options?: Options,
): boolean => {
  const entry = useIntersectionObserver(elementRef, options);

  // When an element is not passed, we consider it visible by default
  if (!hasIntersectionObserverSupport || !elementRef) {
    return true;
  }
  return !!entry?.isIntersecting;
};

/** Whether the browser supports IntersectionObserver. */
const hasIntersectionObserverSupport =
  typeof window.IntersectionObserver !== 'undefined';

/**
 * To handle both refs and elements, always return the element
 * @param ref
 * @returns
 */
const getElement = (ref: IntersectionElement): Element | undefined => {
  if (!ref) return undefined;
  if (is.Element(ref)) return ref;
  return ref.current ?? undefined;
};
